import { API_URL } from "./config"

// Web Audio API Sound Synthesizer & Player Engine
class SoundEngine {
  private ctx: AudioContext | null = null

  // Boshliq (2026-09-03): bildirishnoma ovozini PASAYTIRISH imkoni.
  // Barcha ovozlar shu yagona "master" tugundan o'tadi — shuning uchun balandlik
  // bitta joyda boshqariladi va har bir ovozga alohida kod yozish shart emas.
  private master: GainNode | null = null

  public static readonly VOLUME_KEY = "notif_volume"   // 0..100
  public static readonly DEFAULT_VOLUME = 60

  /** Saqlangan balandlik, 0..1 oralig'ida (ijro uchun) */
  public getVolume(): number {
    try {
      const raw = localStorage.getItem(SoundEngine.VOLUME_KEY)
      const v = raw === null ? SoundEngine.DEFAULT_VOLUME : Number(raw)
      if (Number.isFinite(v) && v >= 0 && v <= 100) return v / 100
    } catch { /* localStorage yopiq bo'lishi mumkin */ }
    return SoundEngine.DEFAULT_VOLUME / 100
  }

  /** Balandlikni saqlaydi (0..100) */
  public setVolume(percent: number) {
    const v = Math.max(0, Math.min(100, Math.round(percent)))
    try { localStorage.setItem(SoundEngine.VOLUME_KEY, String(v)) } catch {}
    if (this.master) this.master.gain.value = v / 100
  }

  /** Barcha ovozlar shu tugunga ulanadi */
  private getMaster(ctx: AudioContext): GainNode {
    if (!this.master) {
      this.master = ctx.createGain()
      this.master.connect(ctx.destination)
    }
    this.master.gain.value = this.getVolume()
    return this.master
  }

  private getAudioContext(): AudioContext | null {
    try {
      if (!this.ctx) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
        if (AudioCtx) {
          this.ctx = new AudioCtx()
        }
      }
      if (this.ctx && this.ctx.state === "suspended") {
        this.ctx.resume().catch(() => {})
      }
      return this.ctx
    } catch {
      return null
    }
  }

  // Plays a synthesized melody using Web Audio API (100% reliable, zero network dependency)
  public playSynthesized(soundKeyOrId: string | number): boolean {
    const ctx = this.getAudioContext()
    if (!ctx) return false

    const now = ctx.currentTime
    const key = String(soundKeyOrId).toLowerCase()

    try {
      // 1. Telegram Chime (Double bell ding: A5 880Hz + A6 1760Hz)
      if (key === "1" || key.includes("telegram")) {
        this.playBellTone(ctx, 880, now, 0.45, 0.4)
        this.playBellTone(ctx, 1320, now + 0.08, 0.4, 0.45)
        return true
      }

      // 2. iPhone Note (Ascending bells C6 -> E6 -> G6)
      if (key === "2" || key.includes("iphone") || key.includes("note")) {
        this.playBellTone(ctx, 1046.5, now, 0.5, 0.35)
        this.playBellTone(ctx, 1318.5, now + 0.09, 0.5, 0.35)
        this.playBellTone(ctx, 1568.0, now + 0.18, 0.6, 0.4)
        return true
      }

      // 3. WhatsApp Pop (Punchy bubble sweep)
      if (key === "3" || key.includes("whatsapp") || key.includes("pop")) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = "sine"
        osc.frequency.setValueAtTime(450, now)
        osc.frequency.exponentialRampToValueAtTime(1250, now + 0.06)

        gain.gain.setValueAtTime(0.01, now)
        gain.gain.linearRampToValueAtTime(0.5, now + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22)

        osc.connect(gain)
        gain.connect(this.getMaster(ctx))
        osc.start(now)
        osc.stop(now + 0.25)
        return true
      }

      // 4. Crystal Drop (Pure glass bell)
      if (key === "4" || key.includes("crystal")) {
        this.playBellTone(ctx, 2093.0, now, 0.55, 0.3)
        this.playBellTone(ctx, 3136.0, now, 0.45, 0.15)
        return true
      }

      // 5. Modern Ping (Executive double blip)
      if (key === "5" || key.includes("ping") || key.includes("modern")) {
        this.playBlip(ctx, 1200, now, 0.05, 0.35)
        this.playBlip(ctx, 1600, now + 0.07, 0.25, 0.4)
        return true
      }

      // 6. Success Harmony (Major chord arpeggio C5-E5-G5-C6)
      if (key === "6" || key.includes("success") || key.includes("harmony")) {
        this.playBellTone(ctx, 523.25, now, 0.6, 0.25)
        this.playBellTone(ctx, 659.25, now + 0.08, 0.6, 0.25)
        this.playBellTone(ctx, 783.99, now + 0.16, 0.6, 0.25)
        this.playBellTone(ctx, 1046.5, now + 0.24, 0.7, 0.35)
        return true
      }

      // 7. Marimba Echo (Wooden warm dual tone)
      if (key === "7" || key.includes("marimba")) {
        this.playMarimbaTone(ctx, 784, now, 0.35, 0.4)
        this.playMarimbaTone(ctx, 1046.5, now + 0.11, 0.4, 0.45)
        return true
      }

      // 8. Subtle Chime (Single minimalist bell ding)
      if (key === "8" || key.includes("subtle") || key.includes("chime")) {
        this.playBellTone(ctx, 1318.51, now, 0.6, 0.35)
        return true
      }

      // 9. Bubble Droplet (Aquatic playful pop)
      if (key === "9" || key.includes("bubble")) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = "sine"
        osc.frequency.setValueAtTime(350, now)
        osc.frequency.exponentialRampToValueAtTime(1300, now + 0.08)
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.2)

        gain.gain.setValueAtTime(0.01, now)
        gain.gain.linearRampToValueAtTime(0.45, now + 0.03)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25)

        osc.connect(gain)
        gain.connect(this.getMaster(ctx))
        osc.start(now)
        osc.stop(now + 0.28)
        return true
      }

      // 10. Gentle Alert (Soft two-tone attention prompt)
      if (key === "10" || key.includes("gentle") || key.includes("alert")) {
        this.playBellTone(ctx, 698.46, now, 0.4, 0.3)
        this.playBellTone(ctx, 1046.5, now + 0.12, 0.55, 0.38)
        return true
      }

      // ---- Boshliq (2026-09-03): yana 10 ta yumshoqroq ovoz ----
      // Barchasi Web Audio bilan sintez qilinadi — fayl yuklanmaydi, kechikish yo'q.

      // 11. Soft Rain (Ikki yumshoq tomchi, past chastota)
      if (key === "11" || key.includes("rain")) {
        this.playBellTone(ctx, 587.33, now, 0.55, 0.26)
        this.playBellTone(ctx, 880.0, now + 0.14, 0.6, 0.22)
        return true
      }

      // 12. Warm Wood (Yog'och taqillashi — issiq, quruq)
      if (key === "12" || key.includes("wood")) {
        this.playWoodTone(ctx, 392.0, now, 0.28, 0.34)
        this.playWoodTone(ctx, 523.25, now + 0.1, 0.3, 0.28)
        return true
      }

      // 13. Calm Bell (Bitta uzun, tinch qo'ng'iroq)
      if (key === "13" || key.includes("calm")) {
        this.playBellTone(ctx, 659.25, now, 1.1, 0.3)
        return true
      }

      // 14. Morning Dew (Uch yumshoq ko'tarilish)
      if (key === "14" || key.includes("dew")) {
        this.playBellTone(ctx, 783.99, now, 0.4, 0.22)
        this.playBellTone(ctx, 987.77, now + 0.1, 0.42, 0.24)
        this.playBellTone(ctx, 1174.66, now + 0.2, 0.5, 0.26)
        return true
      }

      // 15. Soft Knock (Ikki past taqillash — bezovta qilmaydi)
      if (key === "15" || key.includes("knock")) {
        this.playWoodTone(ctx, 261.63, now, 0.22, 0.3)
        this.playWoodTone(ctx, 261.63, now + 0.16, 0.22, 0.24)
        return true
      }

      // 16. Glass Touch (Shisha — toza, qisqa)
      if (key === "16" || key.includes("glass")) {
        this.playBellTone(ctx, 1396.91, now, 0.35, 0.24)
        this.playBellTone(ctx, 2093.0, now + 0.05, 0.3, 0.16)
        return true
      }

      // 17. Low Pulse (Past puls — ochiq idorada bezovta qilmaydi)
      if (key === "17" || key.includes("pulse")) {
        this.playBellTone(ctx, 329.63, now, 0.3, 0.3)
        this.playBellTone(ctx, 329.63, now + 0.22, 0.35, 0.24)
        return true
      }

      // 18. Kalimba (Afrika kalimbasi — mayin)
      if (key === "18" || key.includes("kalimba")) {
        this.playWoodTone(ctx, 880.0, now, 0.45, 0.3)
        this.playWoodTone(ctx, 1174.66, now + 0.12, 0.5, 0.26)
        return true
      }

      // 19. Night Chime (Kechki qo'ng'iroq — juda past, uzun)
      if (key === "19" || key.includes("night")) {
        this.playBellTone(ctx, 440.0, now, 1.3, 0.22)
        this.playBellTone(ctx, 659.25, now + 0.25, 1.0, 0.16)
        return true
      }

      // 20. Quick Tick (Eng qisqa — faqat sezdiradi)
      if (key === "20" || key.includes("tick")) {
        this.playWoodTone(ctx, 1046.5, now, 0.14, 0.26)
        return true
      }


      // Default fallback chime
      this.playBellTone(ctx, 880, now, 0.4, 0.35)
      this.playBellTone(ctx, 1320, now + 0.08, 0.45, 0.4)
      return true
    } catch {
      return false
    }
  }

  /**
   * Yog'och/kalimba tembri (Boshliq 2026-09-03): qo'ng'iroqdan farqli — harmonikasi
   * yo'q, tez so'nadi. Shuning uchun quloqqa yumshoqroq va tez o'tib ketadi.
   */
  private playWoodTone(ctx: AudioContext, freq: number, startTime: number, duration: number, maxVol: number) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = "triangle"
    osc.frequency.setValueAtTime(freq, startTime)
    gain.gain.setValueAtTime(0.001, startTime)
    gain.gain.linearRampToValueAtTime(maxVol, startTime + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)
    osc.connect(gain)
    gain.connect(this.getMaster(ctx))
    osc.start(startTime)
    osc.stop(startTime + duration)
  }

  private playBellTone(ctx: AudioContext, freq: number, startTime: number, duration: number, maxVol: number) {
    const osc = ctx.createOscillator()
    const harmonic = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "sine"
    osc.frequency.setValueAtTime(freq, startTime)

    harmonic.type = "triangle"
    harmonic.frequency.setValueAtTime(freq * 2, startTime)

    gain.gain.setValueAtTime(0.01, startTime)
    gain.gain.linearRampToValueAtTime(maxVol, startTime + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)

    osc.connect(gain)
    harmonic.connect(gain)
    gain.connect(this.getMaster(ctx))

    osc.start(startTime)
    harmonic.start(startTime)
    osc.stop(startTime + duration)
    harmonic.stop(startTime + duration)
  }

  private playBlip(ctx: AudioContext, freq: number, startTime: number, duration: number, maxVol: number) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "sine"
    osc.frequency.setValueAtTime(freq, startTime)

    gain.gain.setValueAtTime(maxVol, startTime)
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)

    osc.connect(gain)
    gain.connect(this.getMaster(ctx))

    osc.start(startTime)
    osc.stop(startTime + duration)
  }

  private playMarimbaTone(ctx: AudioContext, freq: number, startTime: number, duration: number, maxVol: number) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "triangle"
    osc.frequency.setValueAtTime(freq, startTime)

    gain.gain.setValueAtTime(maxVol, startTime)
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)

    osc.connect(gain)
    gain.connect(this.getMaster(ctx))

    osc.start(startTime)
    osc.stop(startTime + duration)
  }

  // Play sound from URL or fall back to synthesis
  public playSound(soundIdOrUrl?: string | number | null) {
    if (soundIdOrUrl === 0 || soundIdOrUrl === "0") return

    // Try synthesized sound first (instant, works 100% on all browsers)
    const success = this.playSynthesized(soundIdOrUrl || 1)
    if (success) return

    // Fallback: try HTML5 Audio
    try {
      if (typeof soundIdOrUrl === "string" && soundIdOrUrl.startsWith("http")) {
        const audio = new Audio(soundIdOrUrl)
        audio.volume = this.getVolume()   // Boshliq: balandlik zaxira yo'lda ham
        audio.play().catch(() => {})
      } else if (typeof soundIdOrUrl === "number" || (typeof soundIdOrUrl === "string" && !isNaN(Number(soundIdOrUrl)))) {
        const baseUrl = API_URL.endsWith("/") ? API_URL : `${API_URL}/`
        const audio = new Audio(`${baseUrl}Musics/audio/${soundIdOrUrl}`)
        audio.volume = this.getVolume()
        audio.play().catch(() => {})
      }
    } catch {}
  }
}

export const soundEngine = new SoundEngine()
